/**
 * FAQ Preview Component
 * Displays FAQ content in various formats with search, analytics, and editing
 */

import React, { useState, useMemo } from 'react';
import {
  HelpCircle, ChevronDown, ChevronUp, Search, ThumbsUp, ThumbsDown,
  Hash, Calendar, User, Copy, Check, Edit2, Save, Plus, Trash2, X
} from 'lucide-react';
import { FAQMetadata } from '../../types/asset-metadata';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface FAQPreviewProps {
  asset: {
    id: number;
    file_path?: string;
    file_name?: string;
    title?: string;
    metadata?: any;
    content?: any;
  };
  apiBaseUrl?: string;
  onSave?: (assetId: number, content: string) => Promise<void>;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category?: string;
  tags?: string[];
  helpful_yes?: number;
  helpful_no?: number;
  related_articles?: string[];
}

export default function FAQPreview({ asset, apiBaseUrl = '', onSave }: FAQPreviewProps) {
  // Parse metadata if it's a string
  const parseMetadata = () => {
    if (typeof asset.metadata === 'string') {
      try {
        return JSON.parse(asset.metadata);
      } catch (e) {
        return {};
      }
    }
    return asset.metadata || {};
  };

  // Also check content field for FAQs
  const parseContent = () => {
    if (typeof asset.content === 'string') {
      try {
        const parsed = JSON.parse(asset.content);
        // If content is an array, it's FAQs directly
        if (Array.isArray(parsed)) {
          return { faqs: parsed };
        }
        return parsed;
      } catch (e) {
        return {};
      }
    }
    if (Array.isArray(asset.content)) {
      return { faqs: asset.content };
    }
    return asset.content || {};
  };

  // Merge metadata from both sources, content takes precedence
  const metadataFromAsset = parseMetadata();
  const contentData = parseContent();
  const initialMetadata: FAQMetadata = {
    ...metadataFromAsset,
    ...contentData,
    faqs: contentData.faqs || metadataFromAsset.faqs || []
  };

  const [metadata, setMetadata] = useState(initialMetadata);
  const [faqs, setFaqs] = useState<FAQ[]>(initialMetadata.faqs || []);
  const [displayFormat, setDisplayFormat] = useState<'accordion'>('accordion');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [helpfulVotes, setHelpfulVotes] = useState<Record<string, { yes: number; no: number }>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingFaq, setEditingFaq] = useState<string | null>(null);
  const [newFaq, setNewFaq] = useState<FAQ | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Quill modules configuration
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  // Initialize helpful votes from metadata (only when FAQ IDs change)
  React.useEffect(() => {
    const votes: Record<string, { yes: number; no: number }> = {};
    faqs.forEach(faq => {
      votes[faq.id] = {
        yes: faq.helpful_yes || 0,
        no: faq.helpful_no || 0
      };
    });
    setHelpfulVotes(votes);
  }, [faqs.map(f => f.id).join(',')]); // Only re-run when FAQ IDs change

  // Filter FAQs based on search
  const filteredFAQs = useMemo(() => {
    if (!searchQuery) return faqs;

    const query = searchQuery.toLowerCase();
    return faqs.filter(faq =>
      faq.question.toLowerCase().includes(query) ||
      faq.answer.toLowerCase().includes(query) ||
      faq.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  }, [faqs, searchQuery]);

  // Toggle FAQ expansion
  const toggleExpanded = (id: string) => {
    if (!id) return; // Safety check
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  // Toggle all FAQs (expand or collapse)
  const toggleAll = (expand: boolean) => {
    if (expand) {
      const allIds = new Set(filteredFAQs.map(faq => faq.id));
      setExpandedItems(allIds);
    } else {
      setExpandedItems(new Set());
    }
  };

  // Handle helpful vote
  const handleVote = (faqId: string, isHelpful: boolean) => {
    setHelpfulVotes(prev => ({
      ...prev,
      [faqId]: {
        yes: prev[faqId]?.yes + (isHelpful ? 1 : 0) || (isHelpful ? 1 : 0),
        no: prev[faqId]?.no + (!isHelpful ? 1 : 0) || (!isHelpful ? 1 : 0)
      }
    }));
  };

  // Copy FAQ to clipboard
  const copyToClipboard = (faq: FAQ) => {
    const text = `Q: ${faq.question}\nA: ${faq.answer}`;
    navigator.clipboard.writeText(text);
    setCopiedId(faq.id);
    setTimeout(() => setCopiedId(null), 2000);
  };


  // Save FAQs
  const handleSave = async () => {
    if (!onSave) {
      alert('Save function not available');
      return;
    }

    setIsSaving(true);
    try {
      const updatedContent = JSON.stringify(faqs);
      await onSave(asset.id, updatedContent);
      // Add a small delay to prevent flashing
      setTimeout(() => {
        setIsEditMode(false);
        setEditingFaq(null);
        setNewFaq(null);
      }, 100);
      alert('FAQs saved successfully!');
    } catch (error) {
      console.error('Failed to save FAQs:', error);
      alert('Failed to save FAQs: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Update FAQ
  const updateFaq = (id: string, updates: Partial<FAQ>) => {
    setFaqs(prev => prev.map(faq =>
      faq.id === id ? { ...faq, ...updates } : faq
    ));
  };

  // Delete FAQ
  const deleteFaq = (id: string) => {
    setFaqs(prev => prev.filter(faq => faq.id !== id));
  };

  // Add new FAQ
  const addFaq = () => {
    const newFaqItem: FAQ = {
      id: Date.now().toString(),
      question: '',
      answer: '',
      tags: []
    };
    setNewFaq(newFaqItem);
    setFaqs(prev => [...prev, newFaqItem]);
    setEditingFaq(newFaqItem.id);
    setExpandedItems(new Set([newFaqItem.id]));
  };

  // FAQ Item Editor
  const FAQEditor = ({ faq }: { faq: FAQ }) => {
    const [question, setQuestion] = useState(faq.question);
    const [answer, setAnswer] = useState(faq.answer);
    const [category, setCategory] = useState(faq.category || '');
    const [tags, setTags] = useState(faq.tags?.join(', ') || '');

    const handleSaveItem = () => {
      updateFaq(faq.id, {
        question,
        answer,
        category,
        tags: tags.split(',').map(t => t.trim()).filter(t => t)
      });
      setEditingFaq(null);
    };

    // Common FAQ categories
    const categories = [
      'General',
      'Product',
      'Pricing',
      'Technical',
      'Installation',
      'Support',
      'Billing',
      'Shipping',
      'Returns',
      'Account',
      'Security',
      'Features'
    ];

    return (
      <div className="space-y-4 p-4 bg-gray-50 rounded">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter question..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select category...</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="tag1, tag2, tag3"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
          <ReactQuill
            value={answer}
            onChange={setAnswer}
            modules={quillModules}
            className="bg-white rounded"
            placeholder="Enter answer..."
          />
        </div>
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => setEditingFaq(null)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveItem}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Save FAQ
          </button>
        </div>
      </div>
    );
  };

  // Accordion View
  const AccordionView = () => (
    <div className="space-y-2">
      {filteredFAQs.map((faq) => (
        <div key={faq.id} className="bg-white rounded-lg shadow-sm border">
          <div
            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 cursor-pointer"
            onClick={() => toggleExpanded(faq.id)}
            style={{ transition: 'background-color 0.15s ease' }}
          >
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">{faq.question || 'New Question'}</h3>
              <div className="flex items-center gap-2 mt-1">
                {faq.category && (
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-medium">
                    {faq.category}
                  </span>
                )}
                {faq.tags && faq.tags.length > 0 && faq.tags.map((tag, i) => (
                  <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {isEditMode && (
                <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setEditingFaq(faq.id)}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                  >
                    <Edit2 className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => deleteFaq(faq.id)}
                    className="p-1 hover:bg-red-100 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              )}
              {expandedItems.has(faq.id) ? (
                <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
              )}
            </div>
          </div>

          {expandedItems.has(faq.id) && (
            <div className="px-6 pb-4 border-t">
              {editingFaq === faq.id ? (
                <FAQEditor faq={faq} />
              ) : (
                <>
                  <div className="pt-4 text-gray-700 prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: faq.answer || '<p>No answer provided yet.</p>' }}
                  />

                  {/* Related Articles */}
                  {faq.related_articles && faq.related_articles.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-semibold text-gray-600 mb-2">Related Articles:</p>
                      <div className="space-y-1">
                        {faq.related_articles.map((article, i) => (
                          <a key={i} href="#" className="text-sm text-blue-600 hover:underline block">
                            {article}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Helpful Section */}
                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-600">Was this helpful?</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleVote(faq.id, true); }}
                        className="flex items-center space-x-1 text-sm text-gray-600 hover:text-green-600 transition-colors"
                      >
                        <ThumbsUp className="w-4 h-4" />
                        <span>{helpfulVotes[faq.id]?.yes || 0}</span>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleVote(faq.id, false); }}
                        className="flex items-center space-x-1 text-sm text-gray-600 hover:text-red-600 transition-colors"
                      >
                        <ThumbsDown className="w-4 h-4" />
                        <span>{helpfulVotes[faq.id]?.no || 0}</span>
                      </button>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(faq); }}
                      className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      {copiedId === faq.id ? (
                        <>
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-green-600">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );



  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <HelpCircle className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold">FAQ Editor</h2>
            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm">
              {faqs.length} Questions
            </span>
          </div>

          <div className="flex items-center space-x-3">
            {/* Edit Mode Toggle */}
            {!isEditMode ? (
              <button
                onClick={() => setIsEditMode(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit FAQs</span>
              </button>
            ) : (
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setIsEditMode(false)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors disabled:bg-gray-400"
                >
                  <Save className="w-4 h-4" />
                  <span>{isSaving ? 'Saving...' : 'Save FAQs'}</span>
                </button>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search FAQs..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="flex items-center space-x-3 ml-4">
            {isEditMode && (
              <button
                onClick={addFaq}
                className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                <Plus className="w-4 h-4" />
                <span>Add FAQ</span>
              </button>
            )}

            <button
              onClick={() => toggleAll(true)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Expand All
            </button>
            <span className="text-gray-400">|</span>
            <button
              onClick={() => toggleAll(false)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Collapse All
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-5xl mx-auto">
          {/* Results Count */}
          {searchQuery && (
            <div className="mb-4 text-sm text-gray-600">
              Found {filteredFAQs.length} of {faqs.length} questions
            </div>
          )}

          {/* FAQ Display */}
          {filteredFAQs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
              <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchQuery ? 'No FAQs found matching your search' : 'No FAQs available'}
              </p>
              {isEditMode && !searchQuery && (
                <button
                  onClick={addFaq}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Add Your First FAQ
                </button>
              )}
            </div>
          ) : (
            <>
              <AccordionView />
            </>
          )}
        </div>
      </div>
    </div>
  );
}